import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  repeatOptions,
  taskDueDates,
  taskDueDatesValues,
} from "../utils/globalConstants";
import moment from 'moment';
import 'moment-timezone'; 
import Select from "react-select";
import ErrorText from "./errorText";
import * as yup from "yup";
import update from "immutability-helper";
import { DatePicker, TimePicker } from "antd";
import Inputs from "./inputs";
import InputMaps from "./InputMaps";
import {
  createNewTask,
  updateTaskbyId,
  getUserOrganizationList,
} from "../services/taskService";
import toast from "react-hot-toast";
import styled from "styled-components";
import _ from "lodash";
import Form from "react-bootstrap/Form";
import ReactAudioPlayer from 'react-audio-player';
import { Logger } from '../utils/logger';
import { FileText, X } from 'lucide-react';
import {
    Root,
    NotesContainer,
    NotesTitle,
    NoteItem,
    NoteContent,
    NoteMeta,
    MetaItem
  } from './TaskFormStyles'; // Save the styles in TaskFormStyles.tsx

interface Props {
  leadId: string; // Now required
  action: string;
  status: string;
  updateTaskValue: any;
  drawerClose: () => void;
  fetchTaskStatusFollowUps: (data?: any) => void;
}

interface taskType {
  task: string;
  type: string;
  dueDate: string | undefined;
  toBePerformAt: string | undefined;
  time: string;
  repeat: string;
  taskAssign: string;
  notes: string;
  customRepeatDays?: number;
  [key: string]: any; // Allow dynamic custom field properties
}
moment.tz.setDefault('Asia/Kolkata');
const TaskForm: React.FC<PropsWithChildren<Props>> = ({
  leadId,
  updateTaskValue,
  action,
  status,
  drawerClose,
  fetchTaskStatusFollowUps,
}) => {
  const StoreData = useSelector((state: any) => {
    return state?.rootReducers;
  });

  // Initialize with enhanced logging
  const [taskData, setTaskData] = useState<taskType>(() => {
    const istNow = moment().tz('Asia/Kolkata');
    return {
      task: leadId || "",
      type: "",
      dueDate: "",
      toBePerformAt: istNow.format(),
      time: istNow.format(),
      repeat: "NoRepeat",
      taskAssign: "",
      notes: "",
      customRepeatDays: undefined,
    };
  });
  

  const [isCustomDate, setIsCustomDate] = useState(false);
  const [taskList, setTaskList] = useState<Array<any>>([]);
  const [taskAssignTo, setTaskAssignTo] = useState<Array<any>>([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  
  const [errors, setErrors] = useState<{[key: string]: string | null}>({
    type: "",
    dueDate: "",
    toBePerformAt: "",
    time: "",
    repeat: "",
    taskAssign: "",
    notes: "",
  });

  // File selection state (not uploaded yet)
  const [selectedFiles, setSelectedFiles] = useState<{[fieldName: string]: File[]}>({});
  const [fileUploading, setFileUploading] = useState<{[fieldName: string]: boolean}>({});

  // Debug component re-renders
  useEffect(() => {
    Logger.log(`🔄 [TaskForm] Component rendered/re-rendered. Selected files:`, selectedFiles);
  });

  // Get custom fields for selected task type
  const getSelectedTaskTypeCustomFields = () => {
    const selectedTaskType = taskList.find(task => task.value === taskData.type);
    return selectedTaskType?.customFields || [];
  };

  // Dynamic validation schema including custom fields
  const createValidationSchema = () => {
    const baseSchema = {
      type: yup.string().required("Task Type is required"),
      dueDate: yup.string().required("Due Date is required"),
      toBePerformAt: yup.string().required("Due Date is required"),
      time: yup.string().required("Time is required"),
    };

    const customFields = getSelectedTaskTypeCustomFields();
    const schemaFields: any = { ...baseSchema };

    customFields
      .filter((field: any) => field.type !== "checkin" && field.type !== "file") // Skip mobile-specific checkin fields and file fields
      .forEach((field: any) => {
      if (field.isRequired) {
        switch (field.type) {
          case 'amount':
          case 'number':
            schemaFields[field.value] = yup.number()
              .min(0, `${field.name} must be a positive number`)
              .required(`${field.name} is required`);
            break;
          case 'selection':
            schemaFields[field.value] = yup.string()
              .required(`Please select ${field.name}`);
            break;
          case 'multi-selection':
            schemaFields[field.value] = yup.array()
              .min(1, `Please select at least one ${field.name}`)
              .required(`Please select ${field.name}`);
            break;
          case 'file':
            schemaFields[field.value] = yup.mixed()
              .test('file-required', `${field.name} is required`, function(value) {
                // Check if file is uploaded (object with fileUrl) or if it's an existing file
                return value && (typeof value === 'object' && (value.fileUrl || value.fileName));
              });
            break;
          case 'location':
            schemaFields[field.value] = yup.object()
              .shape({
                address: yup.string().required(`${field.name} address is required`),
                coords: yup.object().shape({
                  latitude: yup.string().required(),
                  longitude: yup.string().required(),
                  latitudeDelta: yup.string().required(),
                  longitudeDelta: yup.string().required()
                }).required()
              })
              .required(`${field.name} is required`);
            break;
          case 'checkin':
          case 'text':
          default:
            schemaFields[field.value] = yup.string()
              .required(`${field.name} is required`);
            break;
        }
      }
    });

    return yup.object().shape(schemaFields);
  };

  useEffect(() => {
    handleGetUserOrganization();
    
    const taskTypes = StoreData?.user?.userPreferences?.taskType || [];
    setTaskList(taskTypes);
  
    if (action === 'add') {
      Logger.log('Initializing new task defaults');
      
      // Type safe find operation
      const defaultType = taskTypes.find((t: { value: string; name: string }) => 
        t.value.toLowerCase() === 'call' || 
        t.value.toLowerCase() === 'follow up'
      );
  
      // Set tomorrow as default date
      const tomorrow = moment().add(1, 'day');
      
      setTaskData(prev => ({
        ...prev,
        type: defaultType?.value || '',
        dueDate: 'tomorrow',
        toBePerformAt: tomorrow.toISOString(),
        time: tomorrow.toISOString()
      }));
  
      Logger.log('Default task settings:', {
        type: defaultType?.value || 'empty',
        date: tomorrow.format('YYYY-MM-DD'),
        time: tomorrow.format('HH:mm:ss')
      });
    }
  }, []);

  // Edit mode initialization with timezone handling
  useEffect(() => {
    if (action === "edit" && updateTaskValue) {
      const istTime = moment(updateTaskValue.toBePerformAt).tz('Asia/Kolkata');
      
      // Check if repeat is a number (custom days)
      const isCustomRepeat = updateTaskValue.repeat && !isNaN(Number(updateTaskValue.repeat));
      
      // Prepare base task data
      const baseTaskData = {
        task: leadId,
        type: updateTaskValue.type,
        dueDate: "custom",
        toBePerformAt: istTime.format(),
        time: istTime.format(),
        repeat: isCustomRepeat ? "custom" : (updateTaskValue.repeat || "NoRepeat"),
        taskAssign: updateTaskValue?.assignedTo[0]?._id,
        notes: "",
        customRepeatDays: isCustomRepeat ? Number(updateTaskValue.repeat) : undefined,
      };

      // Add custom field data from extraDetails
      const extraDetailsData = updateTaskValue.extraDetails || {};
      
      setTaskData({
        ...baseTaskData,
        ...extraDetailsData
      });
  
      setIsCustomDate(true);
      setIsCompleted(updateTaskValue?.isCompleted ?? false);
    }
  }, [action, updateTaskValue, leadId]);

  
  const notePills = [
    "Callback",
    "Busy", 
    "Msg sent",
    
    "Interested",
    "More Info"
  ];

  
  const handleGetUserOrganization = async () => {
    const data = localStorage.getItem("userData");
    if (data) {
      let tempArray = [...JSON.parse(data).organizationEmployee];
      tempArray.forEach((el: any) => {
        let findRole = StoreData.userData.userDetails.organizationRoles.find(
          (x: any) => x._id == el.role
        ).displayName;
        el.roleName = findRole;
        el.organizationName = StoreData.userData.userDetails.organization.name;
      });
      setTaskAssignTo(tempArray);
    }
  };

  const handleSetDueDate = (dueDateType: string) => {
    Logger.log('Setting due date:', dueDateType);
    
    setErrors({
      ...errors,
      toBePerformAt: "",
    });
  
    switch (dueDateType) {
      case "today":
      case "tomorrow":
      case "3d":
      case "1w":
      case "1m":
        const dt = moment()
          .add(taskDueDatesValues[dueDateType as keyof typeof taskDueDatesValues], "days")
          .format("YYYY-MM-DD");
          
        const existingTime = taskData.time 
          ? moment(taskData.time).format("HH:mm:ss")
          : moment().format("HH:mm:ss");
        
        const combined = moment(dt + "T" + existingTime);
        
        Logger.log('Due date calculation:', {
          type: dueDateType,
          calculatedDate: dt,
          existingTime,
          combined: combined.format()
        });
        
        setTaskData({
          ...taskData,
          toBePerformAt: combined.toISOString(),
          dueDate: dueDateType,
          time: combined.toISOString()
        });
        setIsCustomDate(false);
        setIsDatePickerOpen(false);
        break;
  
      case "custom":
        const currentDateTime = taskData.toBePerformAt 
          ? moment(taskData.toBePerformAt)
          : moment();
          
        Logger.log('Setting custom date:', {
          current: currentDateTime.format()
        });
        
        setTaskData({
          ...taskData,
          toBePerformAt: currentDateTime.toISOString(),
          dueDate: dueDateType,
          time: currentDateTime.toISOString()
        });
        setIsCustomDate(true);
        setIsDatePickerOpen(true);
        break;
  
      default:
        break;
    }
  };
  const handleNotePillAppend = (pill: string) => {
    setTaskData(prev => ({
      ...prev,
      notes: prev.notes 
        ? `${prev.notes}\n${pill}` 
        : pill
    }));
  };
  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.currentTarget;
    Logger.log('Form field change:', { field: name, value });
    
    setTaskData(prev => ({
      ...prev,
      [name]: value,
    }));
    setErrors(prev => ({
      ...prev,
      [name]: null,
    }));
  }, []);

  // Handle custom field changes
  const handleCustomFieldChange = useCallback((name: string, value: string | string[]) => {
    Logger.log('Custom field change:', { field: name, value });
    
    setTaskData(prev => ({
      ...prev,
      [name]: value,
    }));
    setErrors(prev => ({
      ...prev,
      [name]: null,
    }));
  }, []);

  // Handle file selection (not upload yet)
  const handleFileSelect = useCallback((fieldName: string, files: FileList) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    Logger.log(`[TaskForm] Files selected for field: ${fieldName}`, {
      count: fileArray.length,
      files: fileArray.map(f => ({ name: f.name, size: f.size, type: f.type }))
    });

    // Validate file sizes
    const invalidFiles = fileArray.filter(file => file.size > 50 * 1024 * 1024);
    if (invalidFiles.length > 0) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: `Files exceed 50MB limit: ${invalidFiles.map(f => f.name).join(', ')}`
      }));
      return;
    }

    // Store selected files (append to existing)
    Logger.log(`📝 [TaskForm] Updating selectedFiles state for ${fieldName}`, {
      previousState: selectedFiles,
      newFiles: fileArray.map(f => f.name)
    });
    
    setSelectedFiles(prev => {
      const newState = {
        ...prev,
        [fieldName]: [...(prev[fieldName] || []), ...fileArray]
      };
      Logger.log(`📝 [TaskForm] New selectedFiles state:`, newState);
      return newState;
    });

    // Clear any previous errors
    setErrors(prev => ({
      ...prev,
      [fieldName]: null
    }));
  }, [selectedFiles]);

  // Remove selected file
  const removeSelectedFile = useCallback((fieldName: string, fileIndex: number) => {
    setSelectedFiles(prev => ({
      ...prev,
      [fieldName]: prev[fieldName]?.filter((_, index) => index !== fileIndex) || []
    }));
  }, []);

  // Upload files for a specific field
  const uploadFilesForField = useCallback(async (fieldName: string, files: File[]) => {
    if (!files || files.length === 0) {
      Logger.log(`🚫 [TaskForm] No files to upload for ${fieldName}`);
      return [];
    }

    const tempLeadId = leadId || 'temp-task-id';
    Logger.log(`📤 [TaskForm] Starting upload for ${files.length} files in field: ${fieldName}`);
    Logger.log(`📤 [TaskForm] Record ID: ${tempLeadId}`);
    Logger.log(`📤 [TaskForm] Files:`, files.map(f => ({ name: f.name, size: f.size, type: f.type })));

    setFileUploading(prev => ({ ...prev, [fieldName]: true }));

    try {
      const { uploadFilesWithSignedUrl } = await import('../services/utilityService');

      const result = await uploadFilesWithSignedUrl(
        files,
        tempLeadId,
        'task',
        {
          onProgress: (progress) => {
            Logger.log(`[TaskForm] Upload progress for ${fieldName}:`, progress);
          }
        }
      );

      if (result && result.successful) {
        Logger.log(`[TaskForm] Upload completed for ${fieldName}: ${result.successful.length}/${files.length} successful`);
        return result.successful;
      } else {
        Logger.log(`[TaskForm] Upload failed for ${fieldName}:`, result);
        return [];
      }
    } catch (error) {
      Logger.log(`[TaskForm] Upload failed for ${fieldName}:`, error);
      throw error;
    } finally {
      setFileUploading(prev => ({ ...prev, [fieldName]: false }));
    }
  }, [leadId]);

  // Handle map/location field changes
  const handleMapChange = useCallback((name: string, value: string, coords: any) => {
    Logger.log('Location field change:', { field: name, value, coords });
    const locationObject = {
      address: value,
      coords: {
        latitude: coords.lat,
        longitude: coords.lng,
        latitudeDelta: 0.0922,
        longitudeDelta: 0.0922
      }
    };

    setTaskData(prev => ({
      ...prev,
      [name]: locationObject,
    }));
    setErrors(prev => ({
      ...prev,
      [name]: null,
    }));
  }, []);

  const handleDateChange = (
    name: string,
    value: moment.Moment | null,
    type = ""
  ) => {
    if (!value) return;
  
    const istMoment = moment(value).tz('Asia/Kolkata');
  
    if (type === "time") {
      const newTime = istMoment.format("HH:mm");
      const currentDate = moment(taskData.toBePerformAt).tz('Asia/Kolkata').format("YYYY-MM-DD");
      const combined = moment.tz(`${currentDate} ${newTime}`, "YYYY-MM-DD HH:mm", 'Asia/Kolkata');
  
      setTaskData(prev => ({
        ...prev,
        toBePerformAt: combined.format(),
        time: combined.format()
      }));
    } else {
      const newDate = istMoment.format("YYYY-MM-DD");
      const currentTime = moment(taskData.time).tz('Asia/Kolkata').format("HH:mm");
      const combined = moment.tz(`${newDate} ${currentTime}`, "YYYY-MM-DD HH:mm", 'Asia/Kolkata');
  
      setTaskData(prev => ({
        ...prev,
        toBePerformAt: combined.format(),
        time: combined.format()
      }));
    }
  };

  const handleTaskSubmit = useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  
    Logger.log('Task Submit:', {
      formData: taskData,
      localTime: moment(taskData.toBePerformAt).format(),
      utcTime: moment(taskData.toBePerformAt).utc().format()
    });
  
    try {
      const dynamicSchema = createValidationSchema();
      const isFormValid = await dynamicSchema.isValid(taskData, {
        abortEarly: false,
      });
  
      if (!isFormValid) {
        const validationErrors = await dynamicSchema.validate(taskData, { abortEarly: false })
          .catch(err => {
            const errors = err.inner.reduce(
              (acc: any, error: { path: string; message: string }) => ({
                ...acc,
                [error.path]: error.message,
              }),
              {}
            );
            setErrors(prevErrors => update(prevErrors, { $set: errors }));
          });
        return;
      }
  
      const utcTime = moment(taskData.toBePerformAt)
        .tz('Asia/Kolkata')
        .utc()
        .format();
  
      // Determine the repeat value to send
      const getRepeatValue = () => {
        if (taskData.repeat === "NoRepeat" || !taskData.repeat) {
          return null; // Don't send repeat field for no repeat
        }
        if (taskData.repeat === "custom" && taskData.customRepeatDays) {
          return taskData.customRepeatDays.toString();
        }
        return taskData.repeat;
      };

      const repeatValue = getRepeatValue();

      // Upload files before creating task
      const uploadAllFiles = async () => {
        const customFields = getSelectedTaskTypeCustomFields();
        Logger.log(`🔍 [TaskForm] Custom fields found:`, customFields.map((f: any) => ({ name: f.name, type: f.type, value: f.value })));
        Logger.log(`🔍 [TaskForm] Selected files:`, selectedFiles);
        
        const uploadedFileData: any = {};
        
        for (const field of customFields) {
          Logger.log(`🔍 [TaskForm] Processing field: ${field.name} (${field.type})`);
          if (field.type === 'file' && selectedFiles[field.value] && selectedFiles[field.value].length > 0) {
            Logger.log(`✅ [TaskForm] Found files for field ${field.value}: ${selectedFiles[field.value].length} files`);
            try {
              const uploadResults = await uploadFilesForField(field.value, selectedFiles[field.value]);
              if (uploadResults.length > 0) {
                // Store multiple files as array or single file as object
                uploadedFileData[field.value] = uploadResults.length === 1 
                  ? uploadResults[0] 
                  : uploadResults;
                Logger.log(`[TaskForm] Uploaded files for ${field.value}:`, uploadedFileData[field.value]);
              }
            } catch (error) {
              Logger.log(`[TaskForm] Failed to upload files for ${field.value}:`, error);
              throw new Error(`Failed to upload files for ${field.name}: ${(error as Error).message}`);
            }
          }
        }
        
        return uploadedFileData;
      };

      // Preserve existing extraDetails during updates and add custom field data
      const getExtraDetails = (uploadedFileData: any = {}) => {
        const baseExtraDetails = action === "edit" && updateTaskValue?.extraDetails 
          ? { ...updateTaskValue.extraDetails } 
          : {};
        
        // Add custom field data to extraDetails
        const customFields = getSelectedTaskTypeCustomFields();
        const customFieldData: any = {};
        
        customFields
          .filter((field: any) => field.type !== "checkin" && field.type !== "file") // Skip mobile-specific checkin fields and file fields
          .forEach((field: any) => {
          const fieldValue = taskData[field.value];
          
          if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
            // Only include non-empty values for non-file fields
            if (Array.isArray(fieldValue) && fieldValue.length === 0) return;
            customFieldData[field.value] = fieldValue;
          }
        });
        
        return { ...baseExtraDetails, ...customFieldData };
      };

      // Upload files first if any are selected
      Logger.log('📋 [TaskForm] Uploading files before task submission...');
      Logger.log('📋 [TaskForm] Current selectedFiles state:', selectedFiles);
      Logger.log('📋 [TaskForm] Task type selected:', taskData.type);
      
      const uploadedFileData = await uploadAllFiles();
      Logger.log('📋 [TaskForm] File upload results:', uploadedFileData);

      // Separate payloads for create and update
      const payload = action === "add" 
        ? {
            leadIds: [leadId],
            type: taskData.type,
            toBePerformAt: utcTime,
            extraDetails: getExtraDetails(uploadedFileData),
            ...(taskData.taskAssign && { assignedTo: [taskData.taskAssign] }),
            ...(repeatValue && { repeat: repeatValue }),
            ...(taskData.notes && { notes: taskData.notes })
          }
        : {
            type: taskData.type,
            toBePerformAt: utcTime,
            extraDetails: getExtraDetails(uploadedFileData),
            ...(taskData.taskAssign && { assignedTo: [taskData.taskAssign] }),
            ...(repeatValue && { repeat: repeatValue }),
            ...(taskData.notes && { notes: taskData.notes }),
            isCompleted
          };
  
      Logger.log('API Payload:', payload);
  
      const response = action === "add"
        ? await createNewTask(payload)
        : await updateTaskbyId(updateTaskValue._id, payload);
  
      Logger.log('API Response:', response);
  
      if (response?.status) {
        toast.success(response?.data?.message);
        // Clear selected files after successful submission
        Logger.log(`✅ [TaskForm] Task saved successfully, clearing selectedFiles`);
        setSelectedFiles({});
        drawerClose();
        fetchTaskStatusFollowUps(response?.data);
      }
    } catch (err) {
      console.error('Task Submit Error:', err);
      toast.error("Error while handling task");
    }
  }, [taskData, leadId, action, updateTaskValue, isCompleted, drawerClose, fetchTaskStatusFollowUps, selectedFiles, getSelectedTaskTypeCustomFields, uploadFilesForField]);

  const getNotesCreatedBy = useCallback((id: string) => {
    const employee = StoreData?.userData?.userDetails?.organizationEmployee ?? [];
    return employee?.find(({ _id }: any) => _id === id)?.firstName ?? "";
  }, [StoreData?.userData?.userDetails?.organizationEmployee]);

  const renderPreviousNotes = useCallback(() => {
    if (action !== "edit" || !updateTaskValue?.noteLogs?.length) return null;
  
    const sortedNotes = _.orderBy(
      updateTaskValue.noteLogs,
      [(obj) => new Date(obj.createdAt)],
      ["desc"]
    );
  
    Logger.log('Sorted notes:', sortedNotes);
  
    const audioFiles = updateTaskValue.files?.filter(
      (file: any) => file.filePath.endsWith('.m4a')
    );
  
    Logger.log('Audio files found:', audioFiles);
  
    return (
      <NotesContainer>
        <NotesTitle>Previous Notes ({sortedNotes.length + audioFiles.length})</NotesTitle>
        {sortedNotes
          ?.filter((e: any) => e.note)
          .map((e: any, i: number) => (
            <NoteItem key={i}>
              <NoteContent>{e.note}</NoteContent>
              <NoteMeta>
                <MetaItem>{moment(e.createdAt).format("DD MMM")}</MetaItem>
                <MetaItem>{moment(e.createdAt).format("hh:mm a")}</MetaItem>
                <MetaItem>by {getNotesCreatedBy(e?.createdBy)}</MetaItem>
              </NoteMeta>
            </NoteItem>
          ))}
        {audioFiles?.map((audioFile: any, i: number) => (
          <NoteItem key={i}>
            <ReactAudioPlayer
              src={`${StoreData?.userData?.userDetails?.bucketUrl}${audioFile.filePath}`}
              controls
            />
            <NoteMeta>
              <MetaItem>{moment(audioFile.uploadedAt).format("DD MMM")}</MetaItem>
              <MetaItem>{moment(audioFile.uploadedAt).format("hh:mm a")}</MetaItem>
              <MetaItem>by {getNotesCreatedBy(audioFile.uploadedBy)}</MetaItem>
            </NoteMeta>
          </NoteItem>
        ))}
      </NotesContainer>
    );
  }, [updateTaskValue, getNotesCreatedBy, action, StoreData?.userData?.userDetails?.bucketUrl]);

  return (
    <Root>
      <div>
          <div className="form-group">
            <label className="form_label">Task Type</label>
            <select
              className="form-select"
              value={taskData.type}
              onChange={handleChange}
              name="type"
            >
              <option value="">Select Task Type</option>
              {taskList?.map((task, j) => (
                <option value={task?.value} key={j}>
                  {task?.name}
                </option>
              ))}
            </select>
            {errors.type && <ErrorText message={errors.type} />}
          </div>

        <div className="form-group">
  <label className="form_label">Due Date</label>
  <select
    className="form-select"
    value={taskData.dueDate}
    name="dueDate"
    onChange={(e) => handleSetDueDate(e.target.value)}
  >
    <option value="">Select due date</option>
    {taskDueDates.map((dueDate, d) => (
      <option value={dueDate.value} key={d}>
        {dueDate.name}
      </option>
    ))}
  </select>
  {errors.dueDate && <ErrorText message={errors.dueDate} />}
</div>

{/* Time picker should always be visible */}
<div className="form-group">
  <label className="form_label">Time</label>
  <TimePicker
  format="hh:mm A"
  className="select_date" 
  value={moment(taskData.time).tz('Asia/Kolkata')}
  onChange={(time) => {
    if (time) {
      handleDateChange("time", time, "time");
    }
  }}
/>
  {errors.time && <ErrorText message={errors.time} />}
</div>

{/* Custom date picker appears only for custom selection */}
{isCustomDate && (
  <div className="form-group">
    <label className="form_label">Custom Date</label>
    <DatePicker
      format="DD MMM YYYY"
      value={moment(taskData.toBePerformAt)}
      className="select_date"
      open={isDatePickerOpen}
      onOpenChange={(open) => setIsDatePickerOpen(open)}
      onChange={(date) => {
        if (date) {
          handleDateChange("toBePerformAt", moment(date));
          setIsDatePickerOpen(false);
        }
      }}
    />
  </div>
)}

        <div className="form-group">
          <label className="form_label">Repeat</label>
          <select
            value={taskData.repeat}
            onChange={handleChange}
            name="repeat"
            className="form-select"
          >
            {repeatOptions.map((option, o) => (
              <option value={option.value} key={o}>
                {option.name}
              </option>
            ))}
          </select>
        </div>

        {taskData.repeat === "custom" && (
          <div className="form-group">
            <label className="form_label">Custom Repeat (Days)</label>
            <input
              type="number"
              className="form-control"
              placeholder="Enter days (1-364)"
              min="1"
              max="364"
              value={taskData.customRepeatDays || ""}
              onChange={(e) => {
                const value = e.target.value;
                const numValue = parseInt(value);
                if (value === "" || (numValue >= 1 && numValue <= 364)) {
                  setTaskData(prev => ({
                    ...prev,
                    customRepeatDays: value === "" ? undefined : numValue
                  }));
                }
              }}
            />
            <small className="text-muted">Enter number of days between 1 and 364</small>
          </div>
        )}

        <div className="form-group">
          <label className="form_label">Task Assign to</label>
          <select
            value={taskData.taskAssign}
            onChange={handleChange}
            name="taskAssign"
            className="form-select"
          >
            <option value="">Select Task Assign to</option>
            {taskAssignTo?.map((item: any, j: number) => (
              <option value={item._id} key={j}>
                {item.firstName} - {item.roleName} - {item.organizationName}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form_label">Note</label>
          <textarea
            className="form-control note_area"
            value={taskData.notes}
            onChange={handleChange}
            name="notes"
            style={{ height: "120px" }}
            placeholder="Enter Note"
          />
        </div>

        <div className="d-flex justify-content-between mb-2">
  {notePills.map((pill) => (
    <button 
      key={pill} 
      type="button" 
      className="btn btn-outline-secondary flex-grow-1 mx-1"
      style={{ fontSize: '0.8rem', padding: '2px 3px' }}
      onClick={() => handleNotePillAppend(pill)}
    >
      {pill}
    </button>
  ))}
</div>

        {/* Dynamic Custom Fields for Selected Task Type */}
        {getSelectedTaskTypeCustomFields()
          .filter((field: any) => field.type !== "checkin" && field.type !== "file") // Skip mobile-specific checkin fields and file fields
          .map((field: any, index: number) => {
          // Get field value, handling location fields specially
          let finalValue;
          if (field.type === "location") {
            const locationData = taskData[field.value];
            finalValue = locationData?.address || "";
          } else if (field.type === "multi-selection") {
            finalValue = taskData[field.value] || [];
          } else {
            finalValue = taskData[field.value];
          }

          return (
            <div key={index} className="form-group hide_dt select_icon">
              {field.type === "location" ? (
                <InputMaps
                  type={field.type}
                  name={field.value}
                  value={finalValue}
                  locationData={taskData[field.value]}
                  onChange={(name: string, value: string, coords: any) => 
                    handleMapChange(name, value, coords)
                  }
                  className="form-control"
                  label={field.name}
                  isRequired={field.isRequired}
                />
              ) : field.type === "date" ? (
                <div className="form-group">
                  <label className="form-label">
                    {field.name}
                    {field.isRequired && <span style={{ color: "red" }}>*</span>}
                  </label>
                  <input
                    type="date"
                    id="task_date"
                    name="toBePerformAt"
                    className="form-control"
                    value={finalValue || ""}
                    onChange={(e: any) =>
                      handleCustomFieldChange(field.value, e.target.value)
                    }
                    placeholder="Enter Date"
                  />
                </div>
              ) : field.type === "file" ? (
                <div className="form-group">
                  <label className="form-label">
                    {field.name}
                    {field.isRequired && <span style={{ color: "red" }}>*</span>}
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    name={field.value}
                    multiple
                    onChange={(e) => {
                      if (e.target.files) {
                        handleFileSelect(field.value, e.target.files);
                      }
                    }}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                  />
                  
                  {/* Upload status during form submission */}
                  {fileUploading[field.value] && (
                    <div className="mt-2">
                      <div className="d-flex align-items-center">
                        <div 
                          className="spinner-border spinner-border-sm me-2" 
                          role="status"
                          style={{ width: '1rem', height: '1rem' }}
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <small className="text-primary">
                          Uploading files...
                        </small>
                      </div>
                    </div>
                  )}
                  
                  {/* Show selected files (not uploaded yet) */}
                  {selectedFiles[field.value] && selectedFiles[field.value].length > 0 && (
                    <div className="mt-3">
                      <small className="text-muted fw-bold">Selected Files ({selectedFiles[field.value].length}):</small>
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {selectedFiles[field.value].map((file, fileIndex) => {
                          const isImage = file.type.startsWith('image/');
                          const fileUrl = isImage ? URL.createObjectURL(file) : null;
                          
                          return (
                            <div 
                              key={fileIndex} 
                              className="position-relative border rounded p-2 d-flex align-items-center gap-2"
                              style={{ maxWidth: '200px', backgroundColor: '#f8f9fa' }}
                            >
                              {isImage && fileUrl ? (
                                <img 
                                  src={fileUrl}
                                  alt={file.name}
                                  style={{ 
                                    width: '40px', 
                                    height: '40px', 
                                    objectFit: 'cover', 
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => {
                                    // Open file in new tab for preview
                                    window.open(fileUrl, '_blank');
                                  }}
                                />
                              ) : (
                                <FileText size={24} className="text-muted" />
                              )}
                              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <small 
                                  className="text-dark fw-medium d-block"
                                  style={{ 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    whiteSpace: 'nowrap' 
                                  }}
                                  title={file.name}
                                >
                                  {file.name}
                                </small>
                                <small className="text-muted">{(file.size / 1024 / 1024).toFixed(2)} MB</small>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger p-1"
                                onClick={() => removeSelectedFile(field.value, fileIndex)}
                                style={{ lineHeight: 1 }}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Show existing uploaded files if in edit mode */}
                  {taskData[field.value] && (
                    <div className="mt-2">
                      <small className="text-muted fw-bold">Current Files:</small>
                      {Array.isArray(taskData[field.value]) ? (
                        <div className="d-flex flex-wrap gap-2 mt-1">
                          {taskData[field.value].map((file: any, idx: number) => (
                            <div key={idx} className="d-flex align-items-center gap-2 border rounded p-2" style={{ backgroundColor: '#e8f5e8' }}>
                              <FileText size={16} className="text-success" />
                              <small className="text-success fw-medium">{file.fileName}</small>
                              {file.fileUrl && (
                                <a 
                                  href={file.fileUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-success"
                                  style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                                >
                                  View
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : typeof taskData[field.value] === 'object' && taskData[field.value].fileName ? (
                        <div className="d-flex align-items-center gap-2 border rounded p-2 mt-1" style={{ backgroundColor: '#e8f5e8' }}>
                          <FileText size={16} className="text-success" />
                          <small className="text-success fw-medium">{taskData[field.value].fileName}</small>
                          {taskData[field.value].fileUrl && (
                            <a 
                              href={taskData[field.value].fileUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-success"
                              style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                            >
                              View
                            </a>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              ) : (
                <Inputs
                  id={field.value}
                  name={field.value}
                  type={
                    field.type === "phone" || field.type === "amount"
                      ? "number"
                      : field.type
                  }
                  autoComplete="off"
                  className="form-control"
                  placeholder={`Enter ${field.name}`}
                  value={finalValue || ""}
                  onChange={(e: any) =>
                    handleCustomFieldChange(field.value, e.target.value)
                  }
                  label={field.name}
                  isRequired={field.isRequired}
                  options={["selection", "multi-selection"].includes(field.type) ? field.fieldOptions : undefined}
                />
              )}

              {/* Show validation error if field has error and is required */}
              {errors[field.value] && field.isRequired && (
                <ErrorText message={errors[field.value] || ""} />
              )}
              
              {/* Additional error handling for specific field types */}
              {errors[field.value] && !field.isRequired && (
                <ErrorText message={errors[field.value] || ""} />
              )}
            </div>
          );
        })}
        {renderPreviousNotes()}

        {action === "edit" && (
            <Form.Check
              type="checkbox"
              id="task-complete"
              label="Mark as completed"
              className="mark_checkbox mt-3"
              checked={isCompleted}
              onChange={() => setIsCompleted(!isCompleted)}
            />
          )}
        </div>

        <div className="auto_form_btn">
          <button
            type="button"
            onClick={handleTaskSubmit}
            className="btn btn-primary-save"
          >
            {action === "add" ? "Add Task" : "Update Task"}
          </button>
        </div>
     

    </Root>
    );
  
};

export default TaskForm;